import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, Mail, ArrowRight, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { resolvePostAuthRoute } from "@/lib/postAuthRoute";
import { useSeo } from "@/lib/seo";

export default function Connexion() {
  useSeo({
    title: "Espace client · Connexion",
    description: "Connectez-vous à votre espace client My Animation pour gérer votre playlist, votre questionnaire et vos rendez-vous.",
    path: "/connexion",
  });
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const explicitFrom = loc.state?.from;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = await resolvePostAuthRoute(u, { explicitFrom });
      nav(dest, { replace: true });
    } catch (e2) {
      let msg = formatApiError(e2);
      const status = e2?.response?.status;
      const url = e2?.response?.config?.url || e2?.config?.url;
      if (status === 404) {
        msg = `Ressource introuvable (404) sur ${url || "l'API"}. Vider le cache du navigateur (Ctrl+Maj+R / Cmd+Maj+R) puis réessayer.`;
        // Also clear any stale token that may point to an old backend
        try { localStorage.removeItem("ma_token"); } catch { /* ignore */ }
      } else if (status === 401 || status === 403) {
        msg = "E-mail ou mot de passe incorrect.";
      }
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black flex items-center justify-center px-6 py-24 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -left-40 w-[600px] h-[600px] rounded-full bg-[#DCB457]/[0.06] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#DCB457]/[0.04] blur-3xl" />

      <div className="relative w-full max-w-[460px]">
        <Link to="/" className="block text-center mb-10">
          <span className="font-serif-luxe text-3xl md:text-4xl gold-text">My Animation</span>
          <p className="text-[10px] uppercase tracking-[0.32em] text-white/45 mt-2">Espace client</p>
        </Link>

        <div className="card-luxe p-8 md:p-10">
          <h1 className="font-serif-luxe text-3xl md:text-4xl leading-tight">
            Bon retour <span className="italic gold-text">parmi nous.</span>
          </h1>
          <p className="text-white/60 text-sm mt-2 mb-8">
            Connectez-vous pour accéder à votre espace personnel.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/70" strokeWidth={1.2} />
                <input
                  type="email"
                  required
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/60 border border-[#DCB457]/25 px-10 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
                  placeholder="vous@email.fr"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/70" strokeWidth={1.2} />
                <input
                  type="password"
                  required
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black/60 border border-[#DCB457]/25 px-10 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {err && (
              <div className="flex items-start gap-2 border border-red-400/40 bg-red-400/5 px-3 py-2 text-xs text-red-300" data-testid="login-error">
                <AlertCircle size={14} className="mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Connexion…" : "Se connecter"} <ArrowRight size={16} strokeWidth={1.4} />
            </button>
          </form>

          <p className="text-center text-xs text-white/50 mt-8">
            Pas encore de compte ?{" "}
            <Link to="/inscription" data-testid="login-to-register" className="text-[#DCB457] hover:underline">
              Créez le vôtre
            </Link>
          </p>
        </div>

        <p className="text-center text-[10px] uppercase tracking-[0.28em] text-white/40 mt-8">
          <Link to="/" className="hover:text-[#DCB457]">← Retour au site</Link>
        </p>
      </div>
    </main>
  );
}
